import React, { useCallback, useContext } from "react";

import { addDays, subDays } from "date-fns";

import { dispatchAnalyticsTrack } from "~/lib/segment";
import { CtrlOrCmd, isMacos } from "~/lib/utils";
import { scrollToImaginaryEvent, scrollToNow } from "~/ui/calendar/utils/scrolling";
import { Modal } from "~/ui/legacyFellowIndex";

import { GlobalKeyboardListener } from "../common";
import { CalendarPageContext, MeetWithContext } from "./CalendarPage";
import { EventFormContext } from "./EventForm/EventFormContext";
import { SidePanelNavContext } from "./SidePanel";

import styles from "./CalendarShortcuts.module.css";

type Shortcut = {
	name: React.ReactNode;
	keys: string[];
};

const navigation = [
	{ name: "Today", keys: ["T"] },
	{ name: "Next week", keys: ["Right"] },
	{ name: "Previous week", keys: ["Left"] },
	{ name: "Select next event", keys: ["N"] },
	{ name: "Select previous event", keys: ["B"] },
];

const eventDetails = [
	{ name: "Edit event", keys: ["E"] },
	{ name: "Join call", keys: ["J"] },
	{ name: "Open notes", keys: ["Enter"] },
	{ name: "Close", keys: ["Esc"] },
];

const eventCreation = [
	{ name: "Create event", keys: ["C"] },
	{ name: "Save", keys: [CtrlOrCmd, "Enter"] },
	{ name: "Cancel", keys: ["Esc"] },
];

interface CalendarShortcutsProps {
	chooseEvent: (direction: "PREV" | "NEXT") => void;
	isModalOpen: boolean;
	setModalOpen: (open: boolean) => void;
}

export default function CalendarShortcuts({ chooseEvent, isModalOpen, setModalOpen }: CalendarShortcutsProps) {
	const openModal = useCallback(() => setModalOpen(true), [setModalOpen]);
	const closeModal = useCallback(() => setModalOpen(false), [setModalOpen]);

	const { getDefaultTimeState } = useContext(CalendarPageContext);
	const { changeTime } = useContext(EventFormContext);
	const { saveCurrentMeetWithParticipants } = useContext(MeetWithContext);
	const { week, setWeek, navState, navStateDispatch } = useContext(SidePanelNavContext);

	const analyze = useCallback(
		(shortcut: string) => dispatchAnalyticsTrack(`Keyboard shortcut used: ${shortcut}`, { feature: "Calendar" }),
		[],
	);

	const toToday = useCallback(() => {
		analyze("today");
		setWeek(new Date());
		scrollToNow(250);
	}, [analyze, setWeek]);

	const toPrevWeek = useCallback(() => {
		analyze("previous week");
		setWeek(subDays(week, 7));
	}, [analyze, week, setWeek]);

	const toNextWeek = useCallback(() => {
		analyze("next week");
		setWeek(addDays(week, 7));
	}, [analyze, week, setWeek]);

	const openEventCreationPanel = useCallback(() => {
		if (navState.mode === "event_form") return;

		analyze("create event");

		const { start, end, isAllDay } = getDefaultTimeState();
		changeTime({ type: "SET", newStart: start, newEnd: end, isAllDay });
		setWeek(start);
		navStateDispatch({ type: "event_form" });
		saveCurrentMeetWithParticipants();
		scrollToImaginaryEvent(250);
	}, [navState, analyze, changeTime, setWeek, navStateDispatch, getDefaultTimeState, saveCurrentMeetWithParticipants]);

	const selectPrevEvent = useCallback(() => {
		analyze("select previous event");

		chooseEvent("PREV");
	}, [analyze, chooseEvent]);

	const selectNextEvent = useCallback(() => {
		analyze("select next event");

		chooseEvent("NEXT");
	}, [analyze, chooseEvent]);

	const keyboardShortcut = isMacos ? "command+ctrl+/" : "ctrl+alt+/";
	const keyboardShortcutDisplay = isMacos ? (
		<>
			<kbd>⌘</kbd> <kbd>Ctrl</kbd> <kbd>/</kbd>
		</>
	) : (
		<>
			<kbd>Ctrl</kbd> <kbd>Alt</kbd> <kbd>/</kbd>
		</>
	);

	return (
		<>
			<GlobalKeyboardListener sequence={keyboardShortcut} onEvent={openModal} global />

			<GlobalKeyboardListener sequence="t" onEvent={toToday} />
			<GlobalKeyboardListener sequence="left" onEvent={toPrevWeek} />
			<GlobalKeyboardListener sequence="right" onEvent={toNextWeek} />

			<GlobalKeyboardListener sequence="c" onEvent={openEventCreationPanel} />

			<GlobalKeyboardListener sequence="b" onEvent={selectPrevEvent} />
			<GlobalKeyboardListener sequence="n" onEvent={selectNextEvent} />

			{navState.mode === "event_info" && (
				<>
					<GlobalKeyboardListener sequence="shift+tab" onEvent={selectPrevEvent} />
					<GlobalKeyboardListener sequence="tab" onEvent={selectNextEvent} />
				</>
			)}

			<Modal isOpen={isModalOpen} onClose={closeModal} aria-label="Calendar shortcuts" width="34rem">
				<Modal.Header onClose={closeModal}>
					<Modal.Title>Calendar shortcuts</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<small className={styles.toggle}>
						Press {keyboardShortcutDisplay} from the Calendar to toggle this modal.
					</small>
					<div className={styles.body}>
						<div className={styles.column}>
							<ShortcutGroup name="Event details" items={eventDetails} />
							<ShortcutGroup name="Event creation" items={eventCreation} />
						</div>
						<div className={styles.column}>
							<ShortcutGroup name="Navigation" items={navigation} />
						</div>
					</div>
				</Modal.Body>
			</Modal>
		</>
	);
}

function ShortcutGroup({ name, items }: { name: string; items: Shortcut[] }) {
	return (
		<div className={styles.group}>
			<div className={styles.groupName}>{name}</div>
			<div className={styles.items}>
				{items.map(item => (
					<div key={item.keys.join(" + ")} className={styles.item}>
						<div className={styles.itemName}>{item.name}</div>
						<div className={styles.itemCode}>
							{item.keys.map(key => (
								<kbd key={key}>{key}</kbd>
							))}
						</div>
					</div>
				))}
			</div>
		</div>
	);
}
